import { useMemo } from 'react';
import {
  TripDesignerConfigProvider as PackageConfigProvider,
  canOpenExperienceDetail,
} from '@koob/trip-designer';
import { useApp, useLanguage, useSnack } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import search from '@koob/search/src/api';
import { SEARCH_TYPES } from '@koob/search';
import { KoobModal } from '@koob/ui';
import { ActingOrganizationChoice } from 'components/RequireActingOrganization';
import { getAllCountriesByDmcId } from 'api/node/countries';
import { getTrip, saveTrip } from 'api/node/trip';
import { getExperience, getExperiences } from 'api/node/experiences';
import {
  getHotel,
  getHotelsAvailability,
  getRoomsAvailability,
} from 'api/node/hotels';
import { getThemes } from 'api/node/themes';
import { getSegments } from 'api/node/segments';
import { getKinds } from 'api/node/kinds';
import { getCategories } from 'api/node/categories';
import { getStyles } from 'api/node/styles';
import { getSustainableLevels } from 'api/node/sustainableLevels';
import { getOrganizationByToDmc } from 'api/node/toConnectionRequest';
import { useTripStore, sumExperienceExtras } from '@koob/trip-designer';
import { isOwnProduct as isOwnProductOf } from '../containers/TripDesigner/Compose/ownership';
import { decodeId, formatCurrency, stateEnum } from '../utils';

// DMC's own API functions already scope hotel/experience reads to the trip
// being designed (partner marketplace permission checks) — the package never
// names that trip itself, so the wrapper reads it off the store.
const currentTripId = () => useTripStore.getState().tripId;

// One-time migration of the pre-US-053 `tbFiltersCache` entry into the
// shared store, for a browser that already had it before this deploy. Runs at
// module load (this file is imported unconditionally, before any route
// renders), not in a `useEffect` — a child route's own mount effect could
// fire before a provider's effects in React's child-then-parent order, so by
// the time an effect here could migrate the cache, that child would already
// have acted on a still-empty store (see US-050's Codebase Patterns note).
// The key is dropped either way: nothing writes it any more, now that
// filters persist through the store alone.
const cachedTripFilters = localStorage.getItem('tbFiltersCache');
if (cachedTripFilters && !useTripStore.getState().tripFilters) {
  useTripStore.getState().setTripFilters(JSON.parse(cachedTripFilters));
}
localStorage.removeItem('tbFiltersCache');

// KD-3594 (US-042/US-043/US-048) — extras, the trip payload and its
// previous-version snapshot used to live as provider-cached state
// (`tbExtrasCache`, `tbDataCache`, `tbOldDataCache`). They now live in the
// shared trip store (`@koob/trip-designer`). Cleared once here so an old
// draft can't come back; per decision D3, a draft never saved to the server
// is just dropped, no migration.
localStorage.removeItem('tbExtrasCache');
localStorage.removeItem('dmc-trip-designer-storage');
localStorage.removeItem('tbDataCache');
localStorage.removeItem('tbOldDataCache');

// Exported so a test can exercise the real tripId-forwarding wiring against
// a shared logic function (e.g. toTripState) without rendering React.
export const api = {
  getTrip: ({ tripId, versionId }) => getTrip({ tripId, versionId }),
  getExperience: ({ experienceId, locale }) =>
    getExperience({ experienceId, locale, tripId: currentTripId() }),
  getHotel: ({ hotelId, locale }) =>
    getHotel({ hotelId, locale, tripId: currentTripId() }),
  // The app function already unwraps the API's { requestId, expiresAt, data }
  // envelope into { requestId, expiresAt, possibilities }; the config still
  // promises { data } to every consumer, so that result becomes the payload.
  getRoomsAvailability: async (params) => ({
    data: await getRoomsAvailability(params),
  }),
  getHotelsAvailability: (params) => getHotelsAvailability(params),
  getExperiencesPricing: ({ filters, locale }) =>
    getExperiences({ filters, locale }),
  saveTrip: (trip) => saveTrip(trip),
  getThemes: () => getThemes({}),
  getSegments: () => getSegments({}),
  getCategories: () => getCategories({}),
  getKinds: () => getKinds({}),
  getStyles: (params) => getStyles({ ...params }),
  getSustainableLevels: () => getSustainableLevels(),
  getOrganizationByToDmc: (params) => getOrganizationByToDmc(params),
};

// US-059 — DMC's own pricing rule for useTripExperiencePricing:
// `experience.previousBooking` wins over the freshly fetched price, netted
// against whichever of its extras were already booked and confirmed with
// the partner (booking those again would double-count them). Exported so a
// test can exercise the netting math directly (same reasoning as `api`).
export const getExperiencePriceOverride = (experience, extras) => {
  const previousBooking = experience?.previousBooking;
  if (previousBooking?.price === undefined) return undefined;

  const bookedExtras = (
    previousBooking.experiencesBooked?.[0]?.extras ?? []
  ).filter((extra) =>
    [stateEnum.SENT, stateEnum.CONFIRMED].includes(extra.state),
  );

  return {
    price:
      previousBooking.price +
      sumExperienceExtras(extras) -
      sumExperienceExtras(bookedExtras),
    currency: previousBooking.currency,
  };
};

// KD-3594 D4 — a rooms refresh (useRoomSelectionRefresh) tells the user only
// when something changed: a warning for a price change, an error when the
// rooms picked are gone. Both apps map the same two event names to a snack
// here rather than in the package, which never imports i18n or @koob/ui.
const onRoomsEvent = (t, notify) => (name, payload) => {
  if (name === 'rooms.priceChanged') {
    notify(
      t('rooms.priceChanged', {
        hotelName: payload.hotelName,
        previousPrice: formatCurrency({
          amount: payload.previousPrice,
          currency: payload.currency,
        }),
        price: formatCurrency({
          amount: payload.price,
          currency: payload.currency,
        }),
      }),
      { type: 'warning' },
    );
  } else if (name === 'rooms.unavailable') {
    notify(t('rooms.unavailable', { hotelName: payload.hotelName }), {
      type: 'error',
    });
  }
};

const TripDesignerConfigProvider = ({ children }) => {
  const { lang, setLang } = useLanguage();
  const { currentOrganizationIdInt, currentOrganization, locales } = useApp();
  const { t } = useTranslation('tripDesigner');
  const { t: tWizard } = useTranslation('tripWizard');
  const { notify } = useSnack();
  const navigate = useNavigate();
  const dmcId = currentOrganization?.organizationId;
  const isGroup = currentOrganization?.isGroup;

  // Stable: the wizard rebuilds its form config when this changes.
  const designTrip = useMemo(
    () => ({
      t: tWizard,
      // The DMC is the logged-in organisation: no "which DMC" step.
      dmc: { mode: 'fixed', id: dmcId },
      loadCountries: () =>
        getAllCountriesByDmcId({ dmcId }).then((res) =>
          (res?.data ?? []).map((c) => ({
            value: c.id,
            label: c.title,
            position: { lat: parseFloat(c.lat), lng: parseFloat(c.lon) },
          })),
        ),
      loadCities: (query, locale) =>
        search({
          query,
          types: [SEARCH_TYPES.CITY],
          locale,
          fromOrganizationId: decodeId(dmcId),
        }).then((res) =>
          (res?.data ?? []).map((i) => ({
            id: i.id,
            title: i.title,
            kind: i.type,
          })),
        ),
      // The DMC picks the trip language on the name step.
      localeOptions: (locales ?? []).map(({ value, label, alpha2 }) => ({
        value,
        label,
        alpha2,
      })),
      // The brief is the TO's ask: the DMC only reads it.
      briefReadOnly: true,
      replacesTripFilters: true,
      afterSave: ({ tripId, locale }) => {
        setLang(locale);
        // The edit modal opens from the composer itself: navigating there
        // again would remount it. Read from window so the check is never stale.
        if (window.location.pathname !== '/trip-designer/compose') {
          navigate('/trip-designer/compose', { state: { tripId } });
        }
      },
      // A trip belongs to one DMC: the group view asks which one first.
      renderBlocked: isGroup
        ? (onExit) => (
            <KoobModal
              open
              showCloseIcon
              setOpen={(open) => !open && onExit?.()}
            >
              <ActingOrganizationChoice />
            </KoobModal>
          )
        : undefined,
    }),
    [tWizard, dmcId, isGroup, locales, setLang, navigate],
  );

  const isOwnProduct = (product) =>
    isOwnProductOf(product, currentOrganizationIdInt);

  const config = {
    api,
    isOwnProduct,
    // US-035 — partner products are not consultable in detail: without a
    // factsheet the header shows the name alone, never a dead icon.
    getFactsheetUrl: (experience) =>
      experience?.id && canOpenExperienceDetail(experience, isOwnProduct)
        ? `/experiences/${experience.id}?locale=${lang}`
        : null,
    locale: lang,
    onEvent: onRoomsEvent(t, notify),
    // US-033/US-053C: DMC's own pricing rule for useTripTotal —
    // `hotel.previousBooking` wins over the day's live hotel price.
    getHotelPriceOverride: (hotel) => hotel?.previousBooking,
    getExperiencePriceOverride,
    // What DMC's pricing request adds to the plain one: the trip it prices
    // for, the template price while no organisation owns the trip yet, and
    // the partner transitional programs the DMC-scoped list leaves out (the
    // day shows no price without them).
    getExperiencePricingFilters: (tripFilters) => ({
      tripId: tripFilters?.tripId ?? undefined,
      hasTemplatePrice: !tripFilters?.organisationId,
      includePartnerExperiences: true,
    }),
    // The day keeps the raw pricing response, and reuses it while its dates
    // hold: the price survives a proposal sent to TO and a remount.
    storesExperiencePricingResponse: true,
    // A saved extra keeps its own price and stays even if the program's
    // fresh price no longer lists it.
    keepsSavedExtras: true,
    designTrip,
  };

  return (
    <PackageConfigProvider config={config}>{children}</PackageConfigProvider>
  );
};

export default TripDesignerConfigProvider;
